import * as React from 'react';

import { useDebounce } from '@uidotdev/usehooks';
import { LucideLoader2, LucideSearch } from 'lucide-react';

import { cn } from '../../../lib/stylingUtils';
import { Button } from '../buttons/Button';
import { Loading, MaybeEmpty } from '../feedback';
import { Checkbox } from '../forms/Checkbox';

import { useMultiSelectEntries } from './useMultiSelectEntries';

/**
 * Props for the MultiSelectFilter component.
 * Generic component for filtering and selecting multiple items.
 */
export interface MultiSelectFilterProps<T> {
  /** Array of items to display and filter */
  entries: T[] | undefined;

  /** Array of selected items */
  selectedEntries: T[] | undefined;

  /** Callback triggered when search query changes (debounced) */
  onSearch?: (query: string) => void;

  /** Placeholder text for the search input */
  searchPlaceholder?: string;

  /** Whether the component is in a loading state */
  isLoading?: boolean;

  /** Whether the component is fetching items */
  isFetching?: boolean;

  /** Currently selected item keys */
  selectedKeys: Set<React.Key>;

  /** Callback when selection changes */
  onSelectionChange: (keys: Set<React.Key>) => void;

  /** Function to extract unique key from item */
  getEntryKey: (item: T) => React.Key;

  /** Function to extract display label from item */
  getEntryLabel: (item: T, isSelected: boolean) => React.ReactNode;

  /** Callback when Apply button is clicked */
  onDismiss?: () => void;

  /** Callback when Reset button is clicked */
  onReset: () => void;

  /** Additional CSS classes */
  className?: string;
}

export const MultiSelectFilter = <T,>({
  entries: itemsProp,
  onSearch,
  selectedEntries: selectedItems,
  isFetching,
  searchPlaceholder = 'Search...',
  isLoading = false,
  selectedKeys,
  onSelectionChange: onSelectionChangeProp,
  getEntryKey: getItemKey,
  getEntryLabel: getItemLabel,
  onDismiss: onDismissProp,
  onReset: onResetProp,
  className,
}: MultiSelectFilterProps<T>) => {
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const allItemsMap = React.useRef(new Map<React.Key, T>());
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [focusedIndex, setFocusedIndex] = React.useState<number>(0);
  const debouncedSearchQuery = useDebounce(searchQuery.trim(), 300);

  const { filteredEntries, handleSelectionChange, handleDismiss, handleReset } =
    useMultiSelectEntries({
      entries: itemsProp,
      selectedEntries: selectedItems,
      selectedKeys: selectedKeys,
      getEntryKey: getItemKey,
      isFetching,
      isShowingFilteredEntries: !!debouncedSearchQuery && !isFetching,
      onSelectionChange: onSelectionChangeProp,
      onClose: onDismissProp,
      onReset: () => {
        setSearchQuery('');
        setFocusedIndex(0);
        onResetProp();
      },
    });

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filteredEntries.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(Math.min(focusedIndex + 1, filteredEntries.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(Math.max(focusedIndex - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredEntries.length) {
          const item = filteredEntries[focusedIndex];
          const key = getItemKey(item);
          const isCurrentlySelected = selectedKeys.has(key);
          handleItemSelection(item, !isCurrentlySelected);
        }
        break;
      case 'Escape':
        if (searchQuery.length > 0) {
          setSearchQuery('');
          e.stopPropagation();
        } else {
          handleDismiss?.();
        }
        break;

      case ' ':
        e.stopPropagation();
        break;
    }
  };

  // Handle individual item selection
  const handleItemSelection = (item: T, isSelected: boolean) => {
    const key = getItemKey(item);
    const newSelection = new Set(selectedKeys);

    if (isSelected) {
      newSelection.add(key);
    } else {
      newSelection.delete(key);
    }

    handleSelectionChange(newSelection);

    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  // Effect to cache items as they are passed in
  React.useEffect(() => {
    filteredEntries.forEach((item) => {
      allItemsMap.current.set(getItemKey(item), item);
    });
  }, [filteredEntries, getItemKey]);

  // Trigger search when debounced query changes
  React.useEffect(() => {
    onSearch?.(debouncedSearchQuery);
  }, [debouncedSearchQuery, onSearch]);

  React.useEffect(() => {
    if (focusedIndex > filteredEntries.length - 1) {
      setFocusedIndex(0);
    }
  }, [focusedIndex, searchQuery, filteredEntries.length]);

  // Scroll focused item into view
  React.useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[
        focusedIndex
      ] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'instant',
        });
      }
    }
  }, [focusedIndex]);

  return (
    <div
      ref={containerRef}
      className={cn('@MultiSelectFilter flex flex-col', className)}
      tabIndex={-1}
    >
      {/* Search Input */}
      <div
        className="flex w-full items-center gap-2 border-b-2 border-primary p-3"
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
      >
        <LucideSearch size={16} className="shrink-0 opacity-60" />
        <input
          ref={searchInputRef}
          type="text"
          autoFocus
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={searchPlaceholder}
          className="w-full border-none bg-transparent text-sm outline-none"
        />
        {isFetching && (
          <LucideLoader2
            size={16}
            className="shrink-0 animate-spin text-neutral/60"
          />
        )}
      </div>

      {/* Items List */}
      <div className="no-scrollbar flex max-h-[40vh] w-full overflow-y-auto">
        <Loading isLoading={isLoading} className="py-6">
          <MaybeEmpty
            isEmpty={filteredEntries.length === 0}
            title="No options found"
            size="sm"
          >
            <div className="w-full">
              {/* Individual Items */}
              <div ref={listRef} className="flex flex-col py-2">
                {filteredEntries.map((item, index) => {
                  const key = getItemKey(item);
                  const isSelected = selectedKeys.has(key);
                  const isFocused = index === focusedIndex;

                  return (
                    <Checkbox
                      key={String(key)}
                      isSelected={isSelected}
                      onChange={(selected) =>
                        handleItemSelection(item, selected)
                      }
                      onMouseEnter={() => setFocusedIndex(index)}
                      className={cn(
                        'px-3 py-2',
                        isFocused && 'bg-neutral/5 ring-1 ring-neutral/10',
                      )}
                    >
                      <div className="w-full">
                        <span className="truncate">
                          {getItemLabel(item, isSelected)}
                        </span>
                      </div>
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          </MaybeEmpty>
        </Loading>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 border-t border-neutral/10 p-3">
        <Button
          variant="outline"
          onPress={handleReset}
          color="neutral"
          isDisabled={selectedKeys.size === 0}
        >
          Clear
          <span className="rounded bg-neutral/10 px-1.5 text-sm text-neutral/60 tabular-nums">
            {selectedKeys.size}
          </span>
        </Button>
        <Button variant="solid" onPress={handleDismiss}>
          Done
        </Button>
      </div>
    </div>
  );
};
